import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, Upload, Image as ImageIcon } from "lucide-react";
import api, { imageUrl } from "@/lib/api";

export default function AdminDishes() {
  const [dishes, setDishes] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [d, c] = await Promise.all([
        api.get("/dishes", { params: { include_unpublished: true } }),
        api.get("/categories", { params: { include_unpublished: true } }),
      ]);
      setDishes(d.data || []);
      setCategories(c.data || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const catById = useMemo(() => {
    const m = {};
    categories.forEach((c) => (m[c.id] = c));
    return m;
  }, [categories]);

  const save = async (payload, id) => {
    try {
      if (id) {
        await api.patch(`/admin/dishes/${id}`, payload);
        toast.success("Dish updated");
      } else {
        await api.post("/admin/dishes", payload);
        toast.success("Dish created");
      }
      setEditing(null);
      setCreating(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const del = async (d) => {
    if (!window.confirm(`Delete dish "${d.name}"?`)) return;
    try {
      await api.delete(`/admin/dishes/${d.id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const togglePublished = async (d) => save({ published: !d.published }, d.id);

  return (
    <div className="mx-auto max-w-6xl">
      <div className="flex items-end justify-between">
        <div>
          <span className="kbd-eyebrow">Admin</span>
          <h1 className="mt-3 font-serif-display text-3xl font-semibold text-secondary">Dishes</h1>
        </div>
        <button
          data-testid="admin-dish-add-button"
          onClick={() => setCreating(true)}
          disabled={categories.length === 0}
          className="btn-primary"
        >
          <Plus className="h-4 w-4" /> Add dish
        </button>
      </div>

      <div className="mt-8 overflow-hidden rounded-2xl border border-border/70 bg-card" data-testid="admin-dishes-table">
        <table className="w-full text-sm">
          <thead className="border-b border-border/70 bg-muted/50 text-left text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
            <tr>
              <th className="px-4 py-3">Image</th>
              <th className="px-4 py-3">Name</th>
              <th className="px-4 py-3">Category</th>
              <th className="px-4 py-3">Price</th>
              <th className="px-4 py-3">Published</th>
              <th className="px-4 py-3">Popular</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={7} className="px-4 py-6 text-center text-muted-foreground">Loading…</td></tr>
            )}
            {!loading && dishes.length === 0 && (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-muted-foreground" data-testid="admin-dish-empty">
                  No dishes yet. Owner must add dishes for them to appear on the customer site.
                </td>
              </tr>
            )}
            {dishes.map((d) => (
              <tr key={d.id} className="border-b border-border/60 last:border-none" data-testid={`admin-dish-row-${d.id}`}>
                <td className="px-4 py-3">
                  {d.image_id ? (
                    <img src={imageUrl(d.image_id)} alt="" className="h-12 w-16 rounded-lg object-cover" />
                  ) : (
                    <div className="grid h-12 w-16 place-items-center rounded-lg border border-dashed border-primary/40 bg-muted text-[8px] font-semibold uppercase tracking-[0.14em] text-primary/70">
                      PLACEHOLDER
                    </div>
                  )}
                </td>
                <td className="px-4 py-3 font-medium text-secondary">{d.name}</td>
                <td className="px-4 py-3 text-muted-foreground">{catById[d.category_id]?.name || "—"}</td>
                <td className="px-4 py-3">
                  {d.price !== null && d.price !== undefined ? `₹${Number(d.price).toLocaleString("en-IN")}` : <span className="text-muted-foreground">Price on request</span>}
                </td>
                <td className="px-4 py-3">
                  <button
                    data-testid={`admin-dish-toggle-published-${d.id}`}
                    onClick={() => togglePublished(d)}
                    className={`rounded-full px-3 py-1 text-xs font-medium ${
                      d.published ? "bg-accent/20 text-secondary" : "bg-muted text-muted-foreground"
                    }`}
                  >
                    {d.published ? "Published" : "Unpublished"}
                  </button>
                </td>
                <td className="px-4 py-3">
                  <span className={d.popular ? "text-primary" : "text-muted-foreground"}>{d.popular ? "Yes" : "—"}</span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex justify-end gap-2">
                    <button
                      data-testid={`admin-dish-edit-${d.id}`}
                      onClick={() => setEditing(d)}
                      className="grid h-8 w-8 place-items-center rounded-full border border-border/70 hover:bg-muted"
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                    <button
                      data-testid={`admin-dish-delete-${d.id}`}
                      onClick={() => del(d)}
                      className="grid h-8 w-8 place-items-center rounded-full border border-border/70 text-destructive hover:bg-destructive/10"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {(creating || editing) && (
        <DishModal
          initial={editing}
          categories={categories}
          onClose={() => {
            setEditing(null);
            setCreating(false);
          }}
          onSave={(payload) => save(payload, editing?.id)}
        />
      )}
    </div>
  );
}

function DishModal({ initial, categories, onClose, onSave }) {
  const [form, setForm] = useState({
    name: initial?.name || "",
    description: initial?.description || "",
    category_id: initial?.category_id || categories[0]?.id || "",
    price: initial?.price ?? "",
    pricing_unit: initial?.pricing_unit || "",
    availability: initial?.availability ?? true,
    published: initial?.published ?? false,
    seasonal: initial?.seasonal ?? false,
    popular: initial?.popular ?? false,
    image_id: initial?.image_id || null,
    display_order: initial?.display_order ?? 0,
  });
  const [uploading, setUploading] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/images", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      set("image_id", data.id);
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const removeImage = async () => {
    if (!form.image_id) return;
    if (!window.confirm("Remove image?")) return;
    try {
      await api.delete(`/admin/images/${form.image_id}`);
      set("image_id", null);
      toast.success("Image removed");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const submit = (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Name required");
    if (!form.category_id) return toast.error("Category required");
    onSave({
      name: form.name.trim(),
      description: form.description.trim() || null,
      category_id: form.category_id,
      price: form.price === "" ? null : Number(form.price),
      pricing_unit: form.pricing_unit || null,
      availability: form.availability,
      published: form.published,
      seasonal: form.seasonal,
      popular: form.popular,
      image_id: form.image_id || null,
      display_order: Number(form.display_order) || 0,
    });
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4" data-testid="admin-dish-modal">
      <form onSubmit={submit} className="w-full max-w-2xl rounded-2xl border border-border/70 bg-card p-6">
        <div className="flex items-center justify-between">
          <h3 className="font-serif-display text-xl font-semibold text-secondary">
            {initial ? "Edit dish" : "New dish"}
          </h3>
          <button type="button" onClick={onClose} className="grid h-8 w-8 place-items-center rounded-full hover:bg-muted">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="mt-5 grid gap-4 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">Dish image</label>
            <div className="flex items-center gap-4">
              {form.image_id ? (
                <img src={imageUrl(form.image_id)} alt="" className="h-24 w-32 rounded-xl object-cover" data-testid="admin-dish-image-preview" />
              ) : (
                <div className="grid h-24 w-32 place-items-center rounded-xl border border-dashed border-primary/40 bg-muted text-center text-[9px] font-semibold uppercase tracking-[0.14em] text-primary/70">
                  DISH IMAGE — REPLACE BEFORE LAUNCH
                </div>
              )}
              <div className="flex gap-2">
                <label data-testid="admin-dish-upload-label" className="btn-primary cursor-pointer !py-2 !text-xs">
                  <Upload className="h-3.5 w-3.5" />
                  {uploading ? "Uploading…" : form.image_id ? "Replace" : "Upload"}
                  <input data-testid="admin-dish-upload-input" type="file" accept="image/*" className="hidden" onChange={onUpload} />
                </label>
                {form.image_id && (
                  <button data-testid="admin-dish-remove-image" type="button" onClick={removeImage} className="btn-ghost !py-2 !text-xs">
                    <ImageIcon className="h-3.5 w-3.5" /> Remove
                  </button>
                )}
              </div>
            </div>
          </div>

          <F label="Name">
            <input data-testid="admin-dish-name-input" required value={form.name} onChange={(e) => set("name", e.target.value)} className="fmt-input" />
          </F>
          <F label="Category">
            <select data-testid="admin-dish-category-select" required value={form.category_id} onChange={(e) => set("category_id", e.target.value)} className="fmt-input">
              <option value="">—</option>
              {categories.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </F>
          <F label="Description">
            <textarea data-testid="admin-dish-description-input" value={form.description} onChange={(e) => set("description", e.target.value)} rows={2} className="fmt-input" />
          </F>
          <F label="Price (₹) — leave empty for Price on request">
            <input data-testid="admin-dish-price-input" type="number" min="0" step="0.01" value={form.price} onChange={(e) => set("price", e.target.value)} className="fmt-input" />
          </F>
          <F label="Pricing unit (e.g. per plate)">
            <input data-testid="admin-dish-unit-input" value={form.pricing_unit} onChange={(e) => set("pricing_unit", e.target.value)} className="fmt-input" />
          </F>
          <F label="Display order">
            <input data-testid="admin-dish-order-input" type="number" value={form.display_order} onChange={(e) => set("display_order", e.target.value)} className="fmt-input" />
          </F>

          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" data-testid="admin-dish-availability-toggle" checked={form.availability} onChange={(e) => set("availability", e.target.checked)} /> Available
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" data-testid="admin-dish-published-toggle" checked={form.published} onChange={(e) => set("published", e.target.checked)} /> Published
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" data-testid="admin-dish-seasonal-toggle" checked={form.seasonal} onChange={(e) => set("seasonal", e.target.checked)} /> Seasonal
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" data-testid="admin-dish-popular-toggle" checked={form.popular} onChange={(e) => set("popular", e.target.checked)} /> Popular
          </label>
        </div>

        <div className="mt-6 flex justify-end gap-3">
          <button type="button" onClick={onClose} className="btn-ghost">Cancel</button>
          <button type="submit" data-testid="admin-dish-save-button" className="btn-primary">Save</button>
        </div>
      </form>
      <style>{`.fmt-input { width:100%; border-radius:0.75rem; border:1px solid hsl(var(--border)); background:hsl(var(--background)); padding:0.65rem 1rem; font-size:0.875rem; }
      .fmt-input:focus { outline:none; border-color:hsl(var(--primary)); box-shadow:0 0 0 3px hsl(var(--primary) / 0.15); }`}</style>
    </div>
  );
}

function F({ label, children }) {
  return (
    <label className="block text-sm">
      <span className="mb-1.5 block text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">{label}</span>
      {children}
    </label>
  );
}
